<script lang="ts" setup>
import { sendPasswordReset } from '__generated__/sendPasswordReset'

import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'

import { props as propDefinition } from './props'

import { useClient } from '@base/api/useClient'

import { Background } from '@base/_types/Background'
import { Toast, ToastSemantic } from '@base/_types/Toast'
import { useToastsStore } from '@base/store/toastsStore'

import BackgroundWrapper from '@base/components/BackgroundWrapper/BackgroundWrapper.vue'
import ImageWrapper from '@base/components/ImageWrapper/ImageWrapper.vue'
import USButton from '@base/components/USButton/USButton.vue'
import USInputText from '@base/components/USInputText/USInputText.vue'

import SplitContent from '@player/_components/SplitContent/SplitContent.vue'

import twoFriends from '@player/users/assets/two-friends.png'
import blobBg from '@player/users/assets/forgot-password-blob-bg.svg'
import { FORGOT_PASSWORD } from '@player/users/ForgotPassword/api/mutations/forgotPassword'
import { playerTitle } from '@player/_lib/playerTitle'

// TODO check if user is already signed in

// Props
const props = defineProps(propDefinition)

// Static Variables
const client = useClient()
const router = useRouter()
const route = useRoute()
const toastsStore = useToastsStore()

useHead({
  title: playerTitle('Forgot Password')
})

const bgImage: Background = {
  fullWidth: true,
  overflow: true,
  media: [
    {
      type: 'image',
      position: {
        alignHorizontal: 'left_50',
        alignVertical: 'center',
        centerAnchor: false,
        cover: false,
        foreground: false,
        objectPositionHorizontal: 'center',
        objectPositionVertical: 'center'
      },
      imageSet: {
        mobile: {
          fadeIn: true,
          lazy: true,
          src: blobBg
        }
      }
    }
  ]
}

// Inputs

// Outputs
const loading = ref<boolean>(false)
const serverError = ref<boolean>(false)
const success = ref<boolean>(false)
const inputError = ref<boolean>(false)
const email = ref<string>('')
const emailError = ref<string>('')

// Helper Functions
const reset = () => {
  loading.value = false
  serverError.value = false
}

// Query Functions
const handleForgotPassword = async () => {
  reset()

  // Fetch Data
  try {
    if (loading.value) return
    loading.value = true
    success.value = true
    inputError.value = false
    emailError.value = ''
    loading.value = true

    const { data } = await client.mutate<sendPasswordReset>({
      mutation: FORGOT_PASSWORD,
      variables: {
        email: email.value
      },
      fetchPolicy: 'no-cache'
    })

    if (!data) {
      serverError.value = true
    } else if (
      data.sendPasswordReset.__typename === 'SendPasswordResetOutput' &&
      data.sendPasswordReset.result
    ) {
      toastsStore.pushToast({
        content: 'If your email exists, a link will be sent to your inbox',
        icon: 'check-circle',
        semantic: ToastSemantic.Success
      })
    } else if (
      data.sendPasswordReset.__typename === 'SendPasswordResetErrorOutput'
    ) {
      const errors = data.sendPasswordReset.errors

      if (errors.email) {
        emailError.value = errors.email.toString()
        inputError.value = true
      } else {
        serverError.value = true
        toastsStore.pushToast({
          content: 'There was an error with that request',
          icon: 'exclamation-square',
          semantic: ToastSemantic.Error
        })
      }
    } else {
      serverError.value = true
      toastsStore.pushToast({
        content: 'There was an error with that request',
        icon: 'exclamation-square',
        semantic: ToastSemantic.Error
      })
    }
  } catch (e) {
    console.error(e)
    serverError.value = true
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="forgot-password">
    <BackgroundWrapper :background="bgImage" />
    <SplitContent
      :sticky="true"
      top-offset="var(--player-header-height)"
      class="margin-top--5 margin-bottom--5"
    >
      <template #left>
        <div class="forgot-password__form-wrapper">
          <h1 class="heading--h3 margin-bottom--8">Reset Your Password</h1>
          <p class="body--p1--regular margin-bottom--5">
            Receive a secure link with password reset instructions by entering
            your email below.
          </p>
          <form
            class="forgot-password__form"
            @submit.prevent="handleForgotPassword"
          >
            <USInputText
              v-model="email"
              name="email"
              heading="Email"
              class="forgot-password__input"
              input-id="email"
              :error="emailError"
              input-type="email"
              :required="true"
            />
            <button
              class="forgot-password__submit margin-top--5"
              type="submit"
              :disabled="loading"
            >
              <USButton label="Send link" :disabled="loading" />
            </button>
          </form>
        </div>
      </template>
      <template #right>
        <div class="forgot-password__image-right">
          <ImageWrapper :src="twoFriends" aspect-ratio="1" />
        </div>
      </template>
    </SplitContent>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint' as bp;

.forgot-password {
  color: var(--color--neutral-a1);

  &__image-right {
    margin-right: auto;
    margin-left: auto;
    max-width: 370px;
  }
}

:deep(.split-content__content) {
  flex-flow: column-reverse;

  @media (min-width: bp.$breakpoint--tablet) {
    flex-flow: row wrap;
  }
}
</style>
